import type { ReactNode } from "react";
import { X, CheckCircle, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAsyncBanners, type Banner, type BannerStatus } from "@/stores/async-banners";
import { Spinner } from "./Spinner";
import { IconButton } from "./IconButton";

type StatusStyle = {
  container: string;
  icon: () => ReactNode;
};

const statusStyles: Record<BannerStatus, StatusStyle> = {
  loading: {
    container: "border-border-1 bg-bg-2 text-fg-2",
    icon: () => <Spinner size="sm" tone="default" />,
  },
  success: {
    container: "border-accent bg-accent-soft text-fg-2",
    icon: () => <CheckCircle className="size-4 text-accent shrink-0" />,
  },
  error: {
    container: "border-sev-crit bg-sev-crit-bg text-sev-crit",
    icon: () => <XCircle className="size-4 text-sev-crit shrink-0" />,
  },
};

interface AsyncBannerProps {
  banner: Banner;
}

function AsyncBanner({ banner }: AsyncBannerProps) {
  const dismiss = useAsyncBanners((s) => s.dismiss);
  const style = statusStyles[banner.status];

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "flex items-center gap-3 px-4 py-2.5 text-sm border-b",
        style.container,
      )}
    >
      {style.icon()}
      <span className="flex-1">{banner.message}</span>
      <IconButton
        size="sm"
        variant="default"
        onClick={() => dismiss(banner.id)}
        aria-label="Dismiss"
        className="opacity-50 hover:opacity-100"
      >
        <X className="size-3.5" />
      </IconButton>
    </div>
  );
}

interface AsyncBannerStackProps {
  className?: string;
}

export function AsyncBannerStack({ className }: AsyncBannerStackProps) {
  const banners = useAsyncBanners((s) => s.banners);

  if (banners.length === 0) return null;

  return (
    <div className={cn("flex flex-col", className)}>
      {banners.map((banner) => (
        <AsyncBanner key={banner.id} banner={banner} />
      ))}
    </div>
  );
}
